import React from "react";
import { tWithKeyAttr as t } from "../../i18nHelper";
import { formatCredits } from "../../utils/featureCreditPackages";
import { AiCreditCompanyDetailResponseDto } from "../../services/api/plansAdminApi";
import styles from "./aiCreditUsageDetail.module.css";
import i18n from "../../i18n";
import { getPrintDate } from "../../utils/utils";

function usagePct(used: number, limit: number | null): number {
  if (!limit || limit <= 0) return 0;
  return Math.min(100, Math.round((used / limit) * 100));
}

// Credits are decimal(18,4); round up to the nearest 0.1 so tiny nonzero usage
// never displays as 0 alongside the >0 filter that keeps it in the list.
function roundUpTenth(value: number): number {
  return Math.ceil(value * 10) / 10;
}

function barClass(pct: number): string {
  if (pct >= 95) return `${styles.progressBar} ${styles.progressDanger}`;
  if (pct >= 80) return `${styles.progressBar} ${styles.progressWarning}`;
  return styles.progressBar;
}

interface AiCreditUsageDetailProps {
  data: AiCreditCompanyDetailResponseDto;
}

export const AiCreditUsageDetail: React.FC<AiCreditUsageDetailProps> = ({ data }) => {
  const { company, balance, features, users } = data;
  const usersWithUsage = users.filter((u) => u.creditsUsed > 0);
  // The allowance meter reflects balance, not company.creditsUsed: company spend also includes
  // purchased-pack-funded credits and in-flight reservations, which the monthly allowance doesn't track.
  const companyUsed = balance.monthlyQuota != null ? balance.monthlyQuota - balance.allowanceRemaining : company.creditsUsed;
  const companyPct = balance.monthlyQuota != null ? Math.min(100, Math.max(0, Math.round(balance.usedPercent))) : 0;

  return (
    <div className={styles.wrapper}>
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionIcon}>🤖</span>
          <div className={styles.sectionTitleGroup}>
            <span className={styles.sectionTitle}>{t("AI_CREDIT_USAGE_DETAIL_TITLE")}</span>
          </div>
        </div>
        <div className={styles.sectionBody}>
          <div className={styles.quotaRow}>
            <div className={styles.quotaLabelRow}>
              <span className={styles.quotaLabel}>{t("AI_CREDIT_USAGE_PLAN_CREDITS")}</span>
              <span className={styles.quotaNumbers}>
                {formatCredits(roundUpTenth(companyUsed))} / {balance.monthlyQuota != null ? formatCredits(balance.monthlyQuota) : "∞"}
              </span>
            </div>
            {balance.monthlyQuota != null && (
              <div className={styles.progressTrack}>
                <div className={barClass(companyPct)} style={{ width: `${companyPct}%` }} />
              </div>
            )}
            <div className={styles.quotaMeta}>
              {balance.monthlyQuota != null && (
                <>
                  {t("AI_CREDIT_USAGE_PLAN_PERCENT_USED", { percent: companyPct })}
                  {" / "}
                </>
              )}
              {t("AI_CREDIT_USAGE_RESETS_AT", { date: getPrintDate(new Date(company.resetsAt)) })}
            </div>
          </div>

          {/* Purchased packs, shown separately from the plan allowance above — they don't reset on a schedule. */}
          {balance.purchasedBalance > 0 && (
            <div className={styles.quotaRow}>
              <div className={styles.quotaLabelRow}>
                <span className={styles.quotaLabel}>{t("AI_CREDIT_USAGE_PURCHASED_BALANCE")}</span>
                <span className={styles.quotaNumbers}>{formatCredits(roundUpTenth(balance.purchasedBalance))}</span>
              </div>
              <div className={styles.quotaMeta}>
                {t("AI_CREDIT_USAGE_SHARED_POOL", { amount: formatCredits(roundUpTenth(balance.purchasedCompanyBalance)) })}
                {" · "}
                {t("AI_CREDIT_USAGE_SEAT_LOCKED", { amount: formatCredits(roundUpTenth(balance.purchasedSeatBalance)) })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Per-feature breakdown ── */}
      {features.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>📊</span>
            <div className={styles.sectionTitleGroup}>
              <span className={styles.sectionTitle}>{t("AI_CREDIT_USAGE_BY_FEATURE")}</span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            {features.map((f) => (
              <div key={f.featureKey} className={styles.quotaRow}>
                <div className={styles.quotaLabelRow}>
                  <span className={styles.quotaLabel}>{i18n.language === "en" ? f.featureNameEn : f.featureName ?? f.featureKey}</span>
                  <span className={styles.quotaNumbers}>{formatCredits(roundUpTenth(f.credits))} {t("AMOUNT_UNIT_CREDITS")}</span>
                </div>
                <div className={styles.quotaMeta}>
                  {t("CALL_COUNT_LABEL")}: {f.callCount.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Per-user breakdown ── */}
      {usersWithUsage.length > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionIcon}>👥</span>
            <div className={styles.sectionTitleGroup}>
              <span className={styles.sectionTitle}>{t("AI_CREDIT_USAGE_BY_USER")}</span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            {usersWithUsage.map((u) => {
              const pct = usagePct(u.creditsUsed, u.monthlyCredits);
              return (
                <div key={u.userGuid} className={styles.quotaRow}>
                  <div className={styles.quotaLabelRow}>
                    <span className={styles.quotaLabel}>{u.userName ?? u.userGuid}</span>
                    <span className={styles.quotaNumbers}>
                      {formatCredits(roundUpTenth(u.creditsUsed))} / {u.monthlyCredits != null ? formatCredits(u.monthlyCredits) : "∞"}
                    </span>
                  </div>
                  {u.monthlyCredits != null && (
                    <div className={styles.progressTrack}>
                      <div className={barClass(pct)} style={{ width: `${pct}%` }} />
                    </div>
                  )}
                  {u.purchasedBalance != null && (
                    <div className={styles.quotaMeta}>
                      {t("AI_CREDIT_USAGE_PERSONAL_PACK", { amount: formatCredits(roundUpTenth(u.purchasedBalance)) })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
